import React, { useEffect, useMemo, useState } from "react";
import { Box, Alert } from "@mui/material";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import SectionLayout from "../components/SectionLayout";
import ChartCard, { useChartData } from "../components/charts/ChartCard";
import { Grafico, ejes, tooltipStyle, legendProps, barCursor, ChartLoading } from "../components/charts/chartTheme";
import { loadCSV, DATA } from "../services/csvLoader";
import { BRAND, SECTION_COLORS } from "../theme";

const INDICADORES = [
  { id: "cobertura",    label: "Beneficiarios FONASA vs Isapres" },
  { id: "isapre",       label: "Cartera isapres" },
  { id: "pib_total",    label: "Salud % del PIB" },
  { id: "pib_publpriv", label: "Salud pública vs privada en PIB" },
  { id: "per_capita",   label: "Gasto per cápita en salud" },
];

function useCSV(url, registerData = true) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const registerChartData = useChartData();
  useEffect(() => {
    loadCSV(url)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(String(e)); setLoading(false); });
  }, [url]);
  useEffect(() => {
    if (registerData) registerChartData(data);
  }, [data, registerData, registerChartData]);
  return { data, loading, error };
}

/* Beneficiarios FONASA vs ISAPRE */
function GraficoBeneficiarios() {
  const fonasa = useCSV(DATA.salud.fonasaBenef, false);
  const isapre = useCSV(DATA.salud.isapreBenef, false);
  const registerChartData = useChartData();
  const merged = useMemo(() => {
    const years = new Set([
    ...fonasa.data.map((r) => String(r.ANIO || r.Anio || r.anio || r.AÑO)),
    ...isapre.data.map((r) => String(r.ANIO || r.Anio || r.anio || r.AÑO)),
    ]);

    return [...years].filter(Boolean).sort().map((y) => {
      const f = fonasa.data.find((r) => String(r.ANIO || r.Anio || r.anio || r.AÑO) === y);
      const i = isapre.data.find((r) => String(r.ANIO || r.Anio || r.anio || r.AÑO) === y);
      const toN = (v) => parseInt(String(v || "0").replace(/\D/g, ""), 10);
      return {
        año: y,
        FONASA: toN(f?.BENEFICIARIOS || f?.beneficiarios),
        ISAPRE: toN(i?.BENEFICIARIOS || i?.beneficiarios),
      };
    });
  }, [fonasa.data, isapre.data]);

  useEffect(() => {
    registerChartData(fonasa.loading || isapre.loading ? [] : merged);
  }, [fonasa.loading, isapre.loading, merged, registerChartData]);

  if (fonasa.loading || isapre.loading) return <ChartLoading />;

  return (
    <Grafico width="100%" height={300}>
      <BarChart data={merged} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => `${(v / 1e6).toFixed(2)} millones`} />
        <Legend {...legendProps} />
        <Bar dataKey="FONASA" fill={BRAND.nodoBlue} radius={[4, 4, 0, 0]} maxBarSize={48} />
        <Bar dataKey="ISAPRE" fill={BRAND.fesRed} radius={[4, 4, 0, 0]} maxBarSize={48} />
      </BarChart>
    </Grafico>
  );
}

/* Cartera de isapres: cotizantes y cargas por año */
function GraficoCarteraIsapre() {
  const { data, loading, error } = useCSV(DATA.salud.isapreTipo);
  if (loading) return <ChartLoading />;
  if (error) return <Alert severity="warning">Error: {error}</Alert>;

  const años = [...new Set(data.map((r) => r["AÑO"] ?? r.ANIO ?? r.anio))].filter(Boolean).sort();
  const toN = (v) => parseInt(String(v || "0").replace(/\D/g, ""), 10);
  const mapped = años.map((a) => {
    const filas = data.filter((r) => (r["AÑO"] ?? r.ANIO ?? r.anio) === a);
    const cot = filas.find((r) => String(r.COTIZANTE_CARGA || "").toUpperCase().includes("COTIZANTE"));
    const car = filas.find((r) => String(r.COTIZANTE_CARGA || "").toUpperCase().includes("CARGA"));
    return {
      año: a,
      Cotizantes: toN(cot?.POBLACION),
      Cargas: toN(car?.POBLACION),
    };
  });

  return (
    <Grafico width="100%" height={300}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => v.toLocaleString("es-CL")} />
        <Legend {...legendProps} />
        <Bar dataKey="Cotizantes" stackId="a" fill={BRAND.nodoBlue} maxBarSize={56} />
        <Bar dataKey="Cargas" stackId="a" fill={BRAND.fesRed} radius={[4, 4, 0, 0]} maxBarSize={56} />
      </BarChart>
    </Grafico>
  );
}

/* Salud % PIB */
function GraficoPIBSalud(url) {
  return function Inner() {
    const { data, loading, error } = useCSV(url);
    if (loading) return <ChartLoading />;
    if (error) return <Alert severity="warning">Error: {error}</Alert>;

    const col = Object.keys(data[0] || {}).find((c) => /PIB|Salud|valor|privado|público/i.test(c) && c !== "-");
    const yearCol = Object.keys(data[0] || {}).find((c) => c === "-" || /anio|año|year/i.test(c));

    const mapped = data.map((r) => ({
      año: String(r[yearCol] ?? ""),
      valor: parseFloat(String(r[col] ?? "0").replace(",", ".")),
    })).filter((r) => r.año && !isNaN(r.valor));

    return (
      <Grafico width="100%" height={280}>
        <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid {...ejes.grid} />
          <XAxis dataKey="año" {...ejes.x} />
          <YAxis {...ejes.y} />
          <Tooltip {...tooltipStyle} />
          <Line type="monotone" dataKey="valor" stroke={BRAND.nodoBlue} dot strokeWidth={2} />
        </LineChart>
      </Grafico>
    );
  };
}

/* Público vs privado en PIB */
function GraficoPublPriv() {
  const { data, loading, error } = useCSV(DATA.salud.pibPublPriv);
  if (loading) return <ChartLoading />;
  if (error) return <Alert severity="warning">Error: {error}</Alert>;

  const yearCol = Object.keys(data[0] || {}).find((c) => c === "-" || /anio|año|year/i.test(c));
  const mapped = data.map((r) => ({
    año: String(r[yearCol] ?? ""),
    Público:  parseFloat(String(r["Público"] ?? r.publico ?? "0").replace(",", ".")),
    Privado: parseFloat(String(r["Privado"] ?? r.privado ?? "0").replace(",", ".")),
  })).filter((r) => r.año);

  return (
    <Grafico width="100%" height={280}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} />
        <Tooltip {...tooltipStyle} cursor={barCursor} />
        <Legend {...legendProps} />
        <Bar dataKey="Público" fill={BRAND.nodoBlue} stackId="a" maxBarSize={56} />
        <Bar dataKey="Privado" fill={BRAND.fesRed} stackId="a" radius={[4, 4, 0, 0]} maxBarSize={56} />
      </BarChart>
    </Grafico>
  );
}

const GraficoPIBTotal = GraficoPIBSalud(DATA.salud.pibTotal);
const GraficoPerCapitaCte = GraficoPIBSalud(DATA.salud.perCapitaCte);

export default function Salud() {
  return (
    <SectionLayout
      title="Salud"
      description="Cobertura del sistema de salud chileno, gasto público y privado, per cápita y participación en el PIB. Fuentes: FONASA, Superintendencia de Salud y Ministerio de Salud."
      tag="Sistema de Salud"
      palette={SECTION_COLORS.salud}
      indicadores={INDICADORES}
    >
      {/* 1. Beneficiarios */}
      <Box id="cobertura">
        <ChartCard
          title="Beneficiarios FONASA vs Isapres"
          subtitle="Número de personas beneficiarias de FONASA e isapres abiertas. Serie anual."
          fuente="FONASA / Superintendencia de Salud"
          tag="Anual"
          csvFilename="beneficiarios_salud"
          pngFilename="beneficiarios_salud"
        >
          <GraficoBeneficiarios />
        </ChartCard>
      </Box>

      {/* 2. Cartera isapres */}
      <Box id="isapre">
        <ChartCard
          title="Cartera de beneficiarios de isapres"
          subtitle="Personas beneficiarias del sistema isapre, desglosadas en cotizantes y cargas. Serie anual."
          fuente="Superintendencia de Salud"
          tag="Anual"
          csvFilename="cartera_isapre"
          pngFilename="cartera_isapre"
        >
          <GraficoCarteraIsapre />
        </ChartCard>
      </Box>

      {/* 3. Salud total % PIB */}
      <Box id="pib_total">
        <ChartCard
          title="Participación del sector salud total en el PIB"
          subtitle="Gasto total en salud como porcentaje del PIB."
          fuente="Ministerio de Salud — Indicadores Macroeconómicos de Salud (IES)"
          tag="Anual"
          csvFilename="salud_pib_total"
          pngFilename="salud_pib_total"
        >
          <GraficoPIBTotal />
        </ChartCard>
      </Box>

      {/* 4. Público vs privado */}
      <Box id="pib_publpriv">
        <ChartCard
          title="Participación público y privado en el PIB"
          subtitle="Gasto público y privado en salud como porcentaje del PIB."
          fuente="Ministerio de Salud — Indicadores Macroeconómicos de Salud (IES)"
          tag="Anual"
          csvFilename="salud_pib_publpriv"
          pngFilename="salud_pib_publpriv"
        >
          <GraficoPublPriv />
        </ChartCard>
      </Box>

      {/* 5. Per cápita */}
      <Box id="per_capita">
        <ChartCard
          title="Gasto per cápita en salud"
          subtitle="Gasto en salud por persona a precios constantes, corrientes y en PPA."
          fuente="Ministerio de Salud — Indicadores Macroeconómicos de Salud (IES)"
          tag="Anual"
          csvFilename="per_capita_salud"
          pngFilename="per_capita_salud"
        >
          <GraficoPerCapitaCte />
        </ChartCard>
      </Box>
    </SectionLayout>
  );
}
